import { IonButton, IonContent, IonIcon, IonPage, IonRow } from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router';
import { DqText } from 'src/components/dq-text/DqText';
import { FirmwareDetailsCard } from 'src/components/firmware/FirmwareDetailsCard';
import DesktopDetailPageHeader from 'src/components/reusable-components/desktop-header/DesktopDetailPageHeader';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { useSocket, useSocketGet, useSocketList } from 'src/utils/socket';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { trash } from 'ionicons/icons';
import { YesNoModal } from 'src/components/reusable-components/yes-no-modal/YesNoModal';
import { GenericMessages } from 'src/intl/GenericMessages';
import { FirmwareMessages } from 'src/intl/FirmwareMessages';
import { FirmwareStatusActions } from 'src/components/firmware/FirmwareGrid';
import { CreateOrUpdateFirmwareModal } from 'src/components/firmware/CreateOrUpdateFirmwareModal';
import { SocketContextPortal } from 'src/utils/socket/context/portalSocketContext';
import { FirmwareDetailsData } from 'src/components/firmware/types';
import { ListFirmwareTiersRequest, useGetFirmwareTiersQuery } from '../../redux/api/firmwareTiers';
import { Firmware } from '../../models/firmware';
import { ListOrganizationsRequest } from '../../models/organization';
import { useToast } from 'src/components/reusable-components/useToast';
import { useGA4 } from '../../utils/ga4';

export const FirmwareInfoPage: React.FC = () => {
  document.title = 'Firmware Detail'; // Purposely English only for Google Analytics
  useGA4();

  const params = useParams<{ id: string }>();
  const history = useHistory();
  const [present] = useToast();
  const { formatMessage } = useIntl();
  const [yesNoModalOpen, setYesNoModalOpen] = useState<boolean>(false);
  const [createOrUpdateFirmwareModalOpen, setCreateOrUpdateFirmwareModalOpen] = useState<boolean>(false);
  const { state: locationState } = useLocation<{ action?: FirmwareStatusActions }>();

  const { item: firmware } = useSocketGet<'firmware', Firmware>(SocketContextSentinel, 'firmware', params.id, 'V2');

  const [orgRequest] = useState<ListOrganizationsRequest>({
    pageNumber: 0,
    pageSize: 10000,
    statuses: ['active'],
    sort: { order: 'asc', property: 'orgName' },
  });
  const { items: organizations } = useSocketList(SocketContextPortal, 'organizations', orgRequest, 'listV2');

  const [firmwareTierRequest] = useState<ListFirmwareTiersRequest>({
    pageNumber: 0,
    pageSize: 10000,
    sort: { order: 'asc', property: 'tierName' },
  });
  const { data: firmwareTiers } = useGetFirmwareTiersQuery(firmwareTierRequest, {
    refetchOnMountOrArgChange: true,
  });

  const { update: updateFirmware, remove: removeFirmware } = useSocket(SocketContextSentinel, 'firmware');

  useEffect(() => {
    if (locationState?.action === FirmwareStatusActions.CreateSuccessful) {
      history.replace({ state: {} });
      present(
        {
          message: formatMessage(FirmwareMessages.firmwareCreationSuccessful),
        },
        'success',
      );
    }
  }, [formatMessage, history, locationState, present]);

  const handleDeleteFirmware = useCallback(
    async (isYes: boolean) => {
      setYesNoModalOpen(false);
      if (isYes) {
        try {
          await removeFirmware(firmware);
          history.replace('/dev-firmware', { action: FirmwareStatusActions.DeleteSuccessful });
        } catch (err) {
          console.error('Error removing firmware');
        }
      }
    },
    [firmware, history, removeFirmware],
  );

  const handleUpdateFirmwareDetailsSubmit = useCallback(
    async (formikValues: FirmwareDetailsData) => {
      const requestData: Record<string, any> = {
        releaseType: formikValues.deploymentType,
        binFile: formikValues.binFile,
        isHidden: formikValues.archiveFirmware,
        displayName: formikValues.displayName,
        version: formikValues.fwVersion,
        firmwareTierId: formikValues.firmwareTier,
      };
      if (formikValues.deploymentType === 'beta') {
        requestData.beta = formikValues.users;
      }
      if (formikValues.deploymentType === 'custom') {
        requestData.custom = formikValues.organization;
      }
      if (formikValues.deploymentType === 'test') {
        requestData.users = formikValues.users;
        requestData.orgs = formikValues.organizations;
      }

      const request: Record<string, any> = { update: {} };
      request._id = firmware?._id;

      request.update.$set = Object.assign({}, requestData);
      delete request.update.$set._id;
      updateFirmware(request)
        .then(() => {
          setCreateOrUpdateFirmwareModalOpen(false);
          present(
            {
              message: formatMessage(FirmwareMessages.firmwareUpdateSuccessful),
            },
            'success',
          );
        })
        .catch(e => {
          console.log('Error update firmware', e);
        });
    },
    [firmware?._id, formatMessage, present, updateFirmware],
  );

  return (
    <IonPage>
      <DesktopDetailPageHeader
        left
        heading={formatMessage(ControllerMessages.firmware)}
        right={
          <IonButton onClick={() => setYesNoModalOpen(true)}>
            <IonIcon slot="icon-only" color="dark" size="large" icon={trash} />
          </IonButton>
        }
      />
      <IonContent className="controller-info-content">
        <IonRow className="details-heading">
          <DqText variant="h1">{firmware?.displayName}</DqText>
        </IonRow>
        <FirmwareDetailsCard
          onEditFirmwareDetails={() => setCreateOrUpdateFirmwareModalOpen(true)}
          firmware={firmware}
          firmwareTiers={firmwareTiers ?? []}
          organizations={organizations}
        />
      </IonContent>
      <YesNoModal
        isOpen={yesNoModalOpen}
        title={formatMessage(FirmwareMessages.deleteFirmwareTitle)}
        description={formatMessage(FirmwareMessages.deleteFirmwareDescription)}
        yesButtonText={formatMessage(FirmwareMessages.deleteFirmwareButtonText)}
        yesButtonColor="danger"
        noButtonText={formatMessage(GenericMessages.cancelButtonLabel)}
        displayCloseButton={true}
        close={async (isYes: boolean) => await handleDeleteFirmware(isYes)}
      />
      <CreateOrUpdateFirmwareModal
        isOpen={createOrUpdateFirmwareModalOpen}
        onClose={() => setCreateOrUpdateFirmwareModalOpen(false)}
        organizations={organizations}
        firmwareTiers={firmwareTiers ?? []}
        existingFirmwareValues={firmware}
        onSave={(formikValues: FirmwareDetailsData) => handleUpdateFirmwareDetailsSubmit(formikValues)}
      />
    </IonPage>
  );
};
